import { Accordion, ErrorMessage } from '@iwoca/orion';
import clsx from 'clsx';
import { useNumberFormat } from 'hooks/useNumberFormat';
import styles from './DebtSummary.module.css';

type TDebtLine = { label: string; amount: number };

type TProps = {
  requestedAmount: number;
  totalDebt: number;
  remaining: number;
  debts: TDebtLine[];
};

export const DebtSummary = ({
  requestedAmount,
  totalDebt,
  remaining,
  debts,
}: TProps) => {
  const { formatCurrency } = useNumberFormat();
  const format = (value: number) =>
    formatCurrency(value, { fractionDigits: 0 });

  const isOverRequested = remaining < 0;

  return (
    <div className={styles.summary}>
      <div className={styles.row}>
        <span>Requested amount</span>
        <span className={styles.value}>{format(requestedAmount)}</span>
      </div>

      {debts.length > 0 ? (
        <Accordion.Item variant="plain">
          <Accordion.Header className={styles.totalHeader}>
            <Accordion.Title className={styles.totalTitle}>
              Total debt to refinance
              <Accordion.Trigger className={styles.button} />
            </Accordion.Title>
            <span className={styles.value}>{format(totalDebt)}</span>
          </Accordion.Header>
          <Accordion.Content className={styles.breakdown}>
            <div className={styles.breakdownBox}>
              {debts.map((debt, index) => (
                <div key={index} className={styles.debtLine}>
                  <span>{debt.label}</span>
                  <span>{format(debt.amount)}</span>
                </div>
              ))}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      ) : (
        <div className={styles.row}>
          <span>Total debt to refinance</span>
          <span className={styles.value}>{format(totalDebt)}</span>
        </div>
      )}

      <hr className={styles.divider} />
      <div className={styles.row}>
        <span>Requested amount remaining</span>
        <span
          className={clsx(styles.value, { [styles.negative]: isOverRequested })}
        >
          {format(remaining)}
        </span>
      </div>

      {isOverRequested && (
        <ErrorMessage>
          The requested amount should be at least {format(totalDebt)} to cover
          the total debt.
        </ErrorMessage>
      )}
    </div>
  );
};
